// services/scannerService.js
//
// Scanner health, profile controls and the hit ledger
// (crypto-backend-api Wave 3-C, feature 4 + the hits half of feature 13).
//
// This is deliberately a SECOND scanner module beside `services/scannerApi.js`.
// `scannerApi` is the profile CRUD the scanner wizard drives (it POSTs/PUTs
// /api/trading_bots); this one owns the read-only operational surface that the
// new `routes/scanner.js` router added:
//
//   GET  /api/scanner/health                       -> always 200, even when down
//   GET  /api/scanner/profiles/:id                 -> { profile, scanner }
//   POST /api/scanner/profiles/:id/unpause         -> { unpaused, profile, lossStreakCleared }
//   POST /api/scanner/profiles/:id/universe/from-watchlist
//   GET  /api/scanner/hits?profileId=&symbol=&reason=&action=&mode=&since=&limit=&cursor=
//
// ─── Three contract facts that shape the UI ────────────────────────────────
//
// 1. **`GET /api/scanner/health` never 5xxs.** A scanner that is down answers
//    200 with `verdict: 'down'` — because a 500 here would make "the scanner
//    is not running" indistinguishable from "the API is broken". So a REJECTED
//    promise from `getHealth()` means the *API* is unreachable, which is a
//    different banner. `verdict: 'unknown'` means Redis was unreachable, not
//    that the scanner is down: do not alarm on it the same way.
//
// 2. **`null` is unlimited; `0` is zero.** For `max_spawns_per_day` and
//    `stop_after_losses`, `null` means "no cap / never auto-pause" and `0`
//    means literally zero. Rendering a null as `0` (or vice versa) inverts the
//    meaning of the field. `spawnsToday: null` means the Redis read FAILED —
//    render "—", not 0.
//
// 3. **`scanner_mode` has exactly three legal values**, case-sensitive:
//    `live` · `alert` · `dry_run`. The scanner treats ANY unrecognised string
//    as `live`, so a typo'd `"alrt"` would spawn real bots with real money.
//    The API rejects anything else with 400 INVALID_SCANNER_CONTROLS; this
//    module never sends a free-text value.
import http from './http';

// ── Run modes ───────────────────────────────────────────────────────────────

export const SCANNER_MODES = [
  {
    value: 'live',
    label: 'Live',
    help: 'Matches spawn real trading bots with real money.',
  },
  {
    value: 'alert',
    label: 'Alert only',
    help: 'Matches are recorded and pushed to you, but no bot is ever created.',
  },
  {
    value: 'dry_run',
    label: 'Dry run',
    help: 'Matches are recorded with the bot that WOULD have been spawned. Nothing trades.',
  },
];

export const SCANNER_MODE_VALUES = SCANNER_MODES.map((m) => m.value);

export const scannerModeLabel = (value) =>
  SCANNER_MODES.find((m) => m.value === value)?.label || (value ? String(value) : 'Live');

// ── Blocked reasons ─────────────────────────────────────────────────────────
//
// The backend's vocabulary, verbatim. GROUP ON THESE, NEVER RENAME THEM — the
// scanner writes the literal string into scanner_hits.blocked_reason and the
// `?reason=` filter matches it exactly.
export const BLOCKED_REASONS = {
  cooldown: 'This symbol fired again inside the profile’s cooldown window.',
  daily_budget: 'The profile already hit its max spawns for today.',
  max_concurrent: 'The profile already has its maximum number of bots running.',
  capital_cap: 'Spawning would exceed the profile’s total capital cap.',
  insufficient_balance: 'Not enough of the funding asset on the exchange.',
  balance_check_failed: 'The balance check itself failed (exchange or network).',
  duplicate_symbol: 'A bot for this symbol is already running.',
  dedup_check_failed: 'The duplicate check itself failed.',
  market_closed: 'The market for this symbol was closed.',
  capacity_check_failed: 'The capacity check itself failed.',
  reservation_refused: 'Capital could not be reserved for the new bot.',
  spawn_failed: 'The bot was accepted but creating it failed.',
  profile_paused: 'The profile was paused when the match fired.',
  profile_gone: 'The profile was deleted between the match and the spawn.',
  opposite_side_open: 'This pair is already held on the other leg of this two-legged scanner, so it was not opened on this side too.',
  net_exposure: 'Opening this leg would push the gap between the long and short books past the scanner’s net-exposure limit.',
  net_exposure_check_failed: 'The scanner could not read its open long and short books, so it refused the leg rather than guess.',
  funding_skip: 'The perpetual’s funding rate charges this side more than the scanner’s funding limit, so it was skipped.',
  short_not_allowed: 'The Alpaca account cannot short right now (it needs a margin account, $2,000 equity and shorting on), so the short leg was skipped.',
  unspecified: 'The scanner declined to spawn without a specific reason.',
};

export const blockedReasonHelp = (reason) =>
  BLOCKED_REASONS[reason] || 'Unrecognised reason — the scanner may be newer than this screen.';

// ── Health ──────────────────────────────────────────────────────────────────

/**
 * Scanner heartbeat verdict. ALWAYS 200 while the API is up — see note 1.
 * @returns {Promise<object>} { alive, verdict, ageMs, lastTickAt, tickAgeMs, wsPools, ... }
 */
export const getHealth = () => http.get('/api/scanner/health').then((res) => res.data || {});

/**
 * What the live scanner can run (PLAN-TWO-LEGGED F2):
 * `{ twoLegged, source, reasonCode, reason }`. `twoLegged` turns true once the
 * scanner advertises it in its heartbeat (unit T2) or the operator sets
 * SCANNER_TWO_LEGGED_LIVE=1; until then a two-legged profile may be saved paused only.
 * PLAN-STOCK-TWO-LEGGED: `twoLeggedStock` (+ `twoLeggedStockSource`,
 * `twoLeggedStockReasonCode`, `twoLeggedStockReason`) is the same gate for a
 * STOCK two-legged profile (Alpaca margin shorts; the scanner's
 * `two_legged_stock` capability). Absent on an older api — then the stock
 * profile is not judged here.
 */
export const getCapabilities = () => http.get('/api/scanner/capabilities').then((res) => res.data || {});

/**
 * The api's 422s on unpausing / resuming a two-legged STOCK scanner whose
 * Alpaca account cannot short (PLAN-STOCK-TWO-LEGGED A-S1), in plain words —
 * the fallback when the api's own `error` (already plain, with the account's
 * equity figure) is missing. Saving it paused is never refused.
 */
export const TWO_LEGS_ACCOUNT_WORDS = {
  TWO_LEGS_SHORTING_DISABLED: 'Shorting is off on this Alpaca account, so the short leg cannot run. Turn shorting on in Alpaca, or keep the scanner paused.',
  TWO_LEGS_NOT_MARGIN: 'This Alpaca account is a cash account; the short leg needs a margin account. Keep the scanner paused, or turn Both ends off.',
  TWO_LEGS_EQUITY_BELOW_MARGIN_MIN: 'Account equity is below $2,000, the margin minimum for shorting. Keep the scanner paused until it is funded, or turn Both ends off.',
  TWO_LEGS_ACCOUNT_UNKNOWN: 'The account’s margin and shorting status has not been read yet, so the two-legged scanner was not started. Try again in a minute.',
};

/** One of those refusals' words (axios error or response body): the api's `error`, else ours; null for any other code. */
export const twoLegsAccountWords = (errOrBody) => {
  const body = errOrBody && errOrBody.response ? errOrBody.response.data : errOrBody;
  const code = body && typeof body === 'object' ? body.code : null;
  if (!code || !TWO_LEGS_ACCOUNT_WORDS[code]) return null;
  return (typeof body.error === 'string' && body.error.trim()) ? body.error : TWO_LEGS_ACCOUNT_WORDS[code];
};

/** Presentation metadata for a verdict. `severity` is an MUI severity name. */
export const HEALTH_VERDICTS = {
  ok: { label: 'Scanner healthy', severity: 'success' },
  degraded: { label: 'Scanner degraded', severity: 'warning' },
  stale_feed: { label: 'Market feed stale', severity: 'warning' },
  down: { label: 'Scanner down', severity: 'error' },
  unknown: { label: 'Scanner status unknown', severity: 'info' },
};

export const healthMeta = (verdict) =>
  HEALTH_VERDICTS[verdict] || { label: `Scanner: ${verdict || 'unknown'}`, severity: 'info' };

// ── Profile controls ────────────────────────────────────────────────────────

/**
 * The EFFECTIVE scanner config for one profile plus its live counters.
 * @returns {Promise<{profile:object, scanner:object}>}
 */
export const getProfile = (id) =>
  http.get(`/api/scanner/profiles/${encodeURIComponent(id)}`).then((res) => res.data || {});

/**
 * Each scanner's LIFETIME P&L over every bot it started (open and finished):
 * `{ asOf, profiles: [{ profileId, botsStarted, botsRunning, botsOpen,
 * botsFinished, botsFailed, realizedPnl, unrealizedPnl, totalPnl, trades,
 * wins, losses, winRatePct, best, worst, firstStartedAt, lastStartedAt,
 * quote, partial, ...performance }] }`. unrealizedPnl / winRatePct / best /
 * worst / botsOpen / quote may be null; `partial: true` = some finished bots'
 * history could not be read (the headline P&L may be off). An older
 * api answers 404 (the callers fall back to the bots on the page).
 *
 * Performance block (absent on an older api; board.js scannerPerfOf reads it):
 * grossProfit, grossLoss, profitFactor (null when nothing lost → ∞ when
 * grossProfit > 0), avgWin, avgLoss, payoffRatio, expectancy (realized per
 * trade), feesPaid, fundingPaid (+ = paid; null without futures bots),
 * deployedCapital, returnOnDeployedPct (totalPnl ÷ deployedCapital),
 * avgPnlPerBot, maxRealizedDrawdown[Pct], currentRealizedDrawdown[Pct] (of the
 * REALIZED curve, % of deployed capital), avgHoldSeconds, longestWinStreak,
 * longestLossStreak, sides ({ long, short: { trades, wins, losses,
 * winRatePct, pnl } } | null), ledgerTrades, ledgerPartial (the per-trade
 * figures miss some trades, e.g. a reconciled one; the headline is
 * unaffected, so the FE marks only those figures with ≈).
 */
export const getPnlSummary = (profileId = null) =>
  http.get('/api/scanner/pnl-summary', profileId != null ? { params: { profileId } } : undefined)
    .then((res) => res.data || {});

/**
 * Clear the circuit breaker.
 *
 * This also DELs `scanner:losses:{id}`. Without that the streak is still at the
 * threshold and the very next losing bot re-trips the breaker — which is why
 * `lossStreakCleared: false` in the response is worth surfacing rather than
 * swallowing.
 */
export const unpauseProfile = (id) =>
  http.post(`/api/scanner/profiles/${encodeURIComponent(id)}/unpause`, {}).then((res) => res.data || {});

/** Merge the caller's watchlist symbols into a `type:'list'` universe. */
export const universeFromWatchlist = (id, { replace = false } = {}) =>
  http
    .post(`/api/scanner/profiles/${encodeURIComponent(id)}/universe/from-watchlist`, { replace: Boolean(replace) })
    .then((res) => res.data || {});

/**
 * Normalise the three control fields for a POST/PUT /api/trading_bots body.
 *
 * Empty string / undefined -> `null`, which the API stores as UNLIMITED (spawns)
 * or NEVER (losses). A typed `0` is passed through as a real 0, because that is
 * what the user asked for and the API stores it verbatim.
 *
 * @returns {{ok:true, values:object} | {ok:false, errors:string[]}}
 */
export const normalizeScannerControls = ({ scanner_mode, max_spawns_per_day, stop_after_losses } = {}) => {
  const errors = [];
  const values = {};

  if (scanner_mode === '' || scanner_mode == null) {
    values.scanner_mode = null;
  } else if (!SCANNER_MODE_VALUES.includes(scanner_mode)) {
    errors.push(`Scanner mode must be one of ${SCANNER_MODE_VALUES.join(', ')}.`);
  } else {
    values.scanner_mode = scanner_mode;
  }

  const ints = { max_spawns_per_day, stop_after_losses };
  for (const [key, raw] of Object.entries(ints)) {
    const label = key === 'max_spawns_per_day' ? 'Max spawns per day' : 'Stop after losses';
    if (raw === '' || raw == null) { values[key] = null; continue; }
    const n = Number(raw);
    if (!Number.isFinite(n) || !Number.isInteger(n)) { errors.push(`${label} must be a whole number.`); continue; }
    if (n < 0) { errors.push(`${label} cannot be negative — leave it empty for no limit.`); continue; }
    values[key] = n;
  }

  return errors.length ? { ok: false, errors } : { ok: true, values };
};

// ── Hits ────────────────────────────────────────────────────────────────────

/**
 * One page of the scanner hit ledger, newest first.
 *
 * `cursor` is the previous page's `nextCursor` (a descending row id), NOT an
 * offset — rows arriving while the user reads cannot shift the window.
 *
 * ⚠ Rows older than `retentionDays` (default 30) are DELETED by the scanner.
 * Anything that must outlive that has to be copied into the watchlist.
 */
export const listHits = ({ profileId, symbol, reason, action, mode, since, limit = 50, cursor } = {}) => {
  const params = { limit };
  if (profileId) params.profileId = profileId;
  if (symbol) params.symbol = symbol;
  if (reason) params.reason = reason;
  if (action) params.action = action;
  if (mode) params.mode = mode;
  if (since) params.since = since;
  if (cursor) params.cursor = cursor;
  return http.get('/api/scanner/hits', { params }).then((res) => {
    const data = res?.data || {};
    return {
      hits: Array.isArray(data.hits) ? data.hits : [],
      nextCursor: data.nextCursor ?? null,
      hasMore: Boolean(data.hasMore),
      retentionDays: data.retentionDays ?? null,
    };
  });
};

const scannerService = {
  getHealth,
  getCapabilities,
  healthMeta,
  getProfile,
  getPnlSummary,
  unpauseProfile,
  universeFromWatchlist,
  normalizeScannerControls,
  listHits,
  blockedReasonHelp,
  scannerModeLabel,
  SCANNER_MODES,
  SCANNER_MODE_VALUES,
  BLOCKED_REASONS,
  HEALTH_VERDICTS,
  TWO_LEGS_ACCOUNT_WORDS,
  twoLegsAccountWords,
};

export default scannerService;
